/* eslint-disable react-refresh/only-export-components */
import { callStateAtom } from "@/Components/VideoRooms/VideoParts/videoAtoms";
import { CallState } from "@/types/callState";
import classNames from "@/utils/classNames";
import { isAppHostRequest } from "@/utils/hosts";
import { useMediaQuery } from "@react-hook/media-query";
import axios, { AxiosHeaders, AxiosResponse } from "axios";
import { getDefaultStore } from "jotai";
import { useState } from "react";
import { createRoot } from "react-dom/client";
import { FaXmark } from "react-icons/fa6";

const RELEASE_HEADER = "x-app-release";
const FORCE_RELOAD_TOKEN_HEADER = "x-force-reload-token";
const FORCED_RELOAD_DELAY_MS = 1_000 * 60 * 60 * 4;
const LIVE_FRONTEND_CHECK_INTERVAL_MS = 1_000 * 60;
const FORCE_RELOAD_WITHOUT_NEW_FRONTEND_AFTER_MS = 1_000 * 60 * 30;
const FRONTEND_ENTRY = 'script[type="module"][src]';
const FRONTEND_ASSETS = `${FRONTEND_ENTRY}, link[rel="stylesheet"][href]`;

function headerValue(response: AxiosResponse, name: string): string | null {
    const value =
        response.headers instanceof AxiosHeaders
            ? response.headers.get(name)
            : response.headers[name];

    return typeof value === "string" && value !== "" ? value : null;
}

function isInCall() {
    return getDefaultStore().get(callStateAtom) !== CallState.Closed;
}

function frontendAssets(root: ParentNode) {
    return Array.from(
        root.querySelectorAll(FRONTEND_ASSETS),
        (element) =>
            element.getAttribute("src") ?? element.getAttribute("href"),
    );
}

async function isAnotherFrontendLive() {
    const response = await fetch(
        new URL("/index.html", window.location.origin),
        { cache: "no-store" },
    );
    if (response.status !== 200) {
        return false;
    }

    const liveIndex = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
    );
    const runningAssets = new Set(frontendAssets(document));

    return (
        liveIndex.querySelector(FRONTEND_ENTRY) !== null &&
        frontendAssets(liveIndex).some((asset) => !runningAssets.has(asset))
    );
}

function showUpdatePrompt() {
    const updatePromptRoot = document.createElement("div");
    updatePromptRoot.id = "update-prompt-root";
    document.body.appendChild(updatePromptRoot);
    createRoot(updatePromptRoot).render(<UpdatePrompt />);
}

/*
 * App host pods go live minutes before the apex CDN publishes the matching SPA,
 * so a new release or token only counts once the apex's index.html loads assets
 * this page did not. Reloading earlier would pin the tab to the old bundle.
 */
export function watchForAppUpdates() {
    let startingRelease: string | null = null;
    let startingForceReloadToken: string | null = null;
    let newForceReloadTokenSeenAt: number | null = null;
    let hasPromptedForUpdate = false;
    let isNewFrontendLive = false;
    let lastLiveFrontendCheckAt = -Infinity;

    const checkWhetherNewFrontendIsLive = async () => {
        const isCheckDue =
            Date.now() - lastLiveFrontendCheckAt >=
            LIVE_FRONTEND_CHECK_INTERVAL_MS;
        if (!isNewFrontendLive && isCheckDue) {
            lastLiveFrontendCheckAt = Date.now();
            isNewFrontendLive = await isAnotherFrontendLive().catch(
                () => false,
            );
        }

        return isNewFrontendLive;
    };

    const applyUpdate = async (
        hasNewForceReloadToken: boolean,
        hasNewRelease: boolean,
    ) => {
        const isLive = await checkWhetherNewFrontendIsLive();
        if (isInCall()) {
            return;
        }

        const hasWaitedLongEnoughForNewFrontend =
            newForceReloadTokenSeenAt !== null &&
            Date.now() - newForceReloadTokenSeenAt >=
                FORCE_RELOAD_WITHOUT_NEW_FRONTEND_AFTER_MS;

        if (
            hasNewForceReloadToken &&
            (isLive || hasWaitedLongEnoughForNewFrontend)
        ) {
            location.reload();
        } else if (hasNewRelease && isLive && !hasPromptedForUpdate) {
            hasPromptedForUpdate = true;
            showUpdatePrompt();
            setTimeout(() => location.reload(), FORCED_RELOAD_DELAY_MS);
        }
    };

    return (response: AxiosResponse) => {
        if (!isAppHostRequest(response.config.url)) {
            return response;
        }

        const release = headerValue(response, RELEASE_HEADER);
        const forceReloadToken = headerValue(
            response,
            FORCE_RELOAD_TOKEN_HEADER,
        );
        startingRelease ??= release;
        startingForceReloadToken ??= forceReloadToken;

        if (isInCall()) {
            return response;
        }

        const hasNewForceReloadToken =
            forceReloadToken !== null &&
            forceReloadToken !== startingForceReloadToken;
        const hasNewRelease =
            release !== null &&
            release !== startingRelease &&
            !hasPromptedForUpdate;
        if (hasNewForceReloadToken) {
            newForceReloadTokenSeenAt ??= Date.now();
        }
        if (hasNewForceReloadToken || hasNewRelease) {
            void applyUpdate(hasNewForceReloadToken, hasNewRelease);
        }

        return response;
    };
}

axios.interceptors.response.use(watchForAppUpdates());

function UpdatePrompt() {
    const isSmall = useMediaQuery("(max-width: 1024px)");

    const [isPromptVisible, setIsPromptVisible] = useState(true);

    const hidePrompt = () => {
        setIsPromptVisible(false);
        setTimeout(
            () => {
                setIsPromptVisible(true);
            },
            1_000 * 60 * 60,
        );
    };

    return isPromptVisible ? (
        <div
            data-testid="prompt-update-reload-modal"
            className={classNames(
                "fixed right-0 m-8",
                isSmall ? "top-0" : "bottom-0",
            )}
            style={{ zIndex: 1000000 }}
        >
            <div
                data-testid="close-button"
                className="absolute -right-2 -top-2 cursor-pointer rounded-full border-2 border-brand-black bg-brand-white hover:bg-brand-gray hover:text-brand-white"
                onClick={() => hidePrompt()}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        hidePrompt();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label="Close notification"
            >
                <FaXmark className="text-md" />
            </div>
            <div className="flex items-center rounded-lg bg-brand-white p-4 shadow-lg">
                <div className="max-w-sm text-sm">
                    A new version of this page is available.{" "}
                    <span
                        className="cursor-pointer underline hover:text-brand-red"
                        onClick={() => location.reload()}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                location.reload();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        Refresh page
                    </span>
                </div>
            </div>
        </div>
    ) : null;
}
